import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Mail, Lock, Shield, ArrowLeft, Key } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Logo } from '../components/Logo';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useForcedDarkMode } from '../contexts/ThemeContext';
import SSOLoginButton from '../components/SSOLoginButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  // Force dark mode on login page
  useForcedDarkMode();
  
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [pendingToken, setPendingToken] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const navigate = useNavigate();
  const { setUser, setToken } = useAuth();

  // Helper to redirect based on onboarding status
  const redirectAfterLogin = (user) => {
    // Check if user needs to complete onboarding
    if (!user.onboarding_completed) {
      const module = user.primary_module || 'grc';
      if (module === 'cosec') {
        navigate('/cosec/onboarding');
      } else {
        // For other modules, go to dashboard (can add module-specific onboarding later)
        navigate('/dashboard');
      }
    } else {
      navigate('/dashboard');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/v2/login`, formData);
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        setRequires2FA(true);
        setPendingToken(response.data.pending_token);
        toast.info('Two-factor authentication required');
      } else {
        // Normal login
        localStorage.setItem('session_token', response.data.session_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setToken(response.data.session_token);
        setUser(response.data.user);
        toast.success('Welcome back!');
        redirectAfterLogin(response.data.user);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    if (!twoFactorCode || twoFactorCode.length < 6) {
      toast.error('Please enter a valid code');
      return;
    }
    
    setLoading(true);
    try {
      const endpoint = useBackupCode ? '/auth/2fa/use-backup-code' : '/auth/2fa/verify-login';
      const payload = useBackupCode 
        ? { pending_token: pendingToken, backup_code: twoFactorCode }
        : { pending_token: pendingToken, code: twoFactorCode };
      
      const response = await axios.post(`${API}${endpoint}`, payload);
      
      localStorage.setItem('session_token', response.data.session_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setToken(response.data.session_token);
      setUser(response.data.user);
      
      if (response.data.warning) {
        toast.warning(response.data.warning);
      } else {
        toast.success('Welcome back!');
      }
      
      redirectAfterLogin(response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setRequires2FA(false);
    setPendingToken(null);
    setTwoFactorCode('');
    setUseBackupCode(false);
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // 2FA Verification Screen
  if (requires2FA) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4" data-testid="2fa-verification-page">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex justify-center mb-6">
              <Logo className="h-12" />
            </Link>
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="p-3 rounded-full bg-primary/10">
                <Shield className="h-6 w-6 text-primary" />
              </div>
            </div>
            <h1 className="text-2xl font-semibold mb-2 font-display" data-testid="2fa-title">
              Two-Factor Authentication
            </h1>
            <p className="text-muted-foreground font-body">
              {useBackupCode 
                ? 'Enter one of your backup codes' 
                : 'Enter the 6-digit code from your authenticator app'
              }
            </p>
          </div>

          <div className="border border-border rounded-lg p-5 sm:p-8 bg-card">
            <form onSubmit={handle2FAVerify} data-testid="2fa-form">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="twoFactorCode">
                    {useBackupCode ? 'Backup Code' : 'Verification Code'}
                  </Label>
                  <div className="relative mt-1.5">
                    {useBackupCode ? (
                      <Key className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Shield className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    )}
                    <Input
                      id="twoFactorCode"
                      type="text"
                      placeholder={useBackupCode ? "XXXXXXXX" : "000000"}
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(
                        useBackupCode 
                          ? e.target.value.toUpperCase() 
                          : e.target.value.replace(/\D/g, '').slice(0, 6)
                      )}
                      className="pl-10 text-center text-xl tracking-widest"
                      maxLength={useBackupCode ? 8 : 6}
                      required
                      autoFocus
                      data-testid="2fa-code-input"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-11 bg-[#2D7D6F] text-white hover:bg-[#236B5F] font-semibold" 
                  disabled={loading}
                  data-testid="2fa-verify-btn"
                >
                  {loading ? 'Verifying...' : 'Verify'}
                </Button>
              </div>
            </form>

            <div className="mt-4 pt-4 border-t border-border space-y-2">
              <button
                type="button"
                onClick={() => setUseBackupCode(!useBackupCode)}
                className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
                data-testid="toggle-backup-code"
              >
                {useBackupCode 
                  ? '← Use authenticator app instead' 
                  : 'Lost access? Use a backup code →'
                }
              </button>
              
              <button
                type="button"
                onClick={handleBack}
                className="w-full flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                data-testid="back-to-login"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Standard Login Screen
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4" data-testid="login-page">
      <Helmet>
        <title>Login | Simplif-i</title>
        <meta name="description" content="Sign in to your Simplif-i workspace for governance, company secretarial, contracts, projects, procurement and M&A." />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://simplif-i.com/login" />
      </Helmet>

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex justify-center mb-6">
            <Logo className="h-12" />
          </Link>
          <h1 className="text-2xl sm:text-3xl font-semibold mb-2 font-display" data-testid="login-title">Welcome back</h1>
          <p className="text-muted-foreground font-body">Sign in to your Simplif-i workspace</p>
        </div>

        <div className="border border-border rounded-lg p-5 sm:p-8 bg-card">
          <Button 
            variant="outline" 
            className="w-full mb-4 h-11" 
            onClick={handleGoogleLogin}
            type="button"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>
          
          {/* Microsoft 365 SSO Button - Only shows if configured */}
          <SSOLoginButton 
            className="mb-6 h-11"
            onLoginSuccess={(user) => {
              localStorage.setItem('user', JSON.stringify(user));
              setUser(user);
              toast.success('Welcome back!');
              redirectAfterLogin(user);
            }}
            onLoginError={(error) => {
              toast.error(error);
            }}
          />

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">Or continue with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} data-testid="login-form">
            <div className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10"
                    required
                    data-testid="password-input"
                  />
                </div>
              </div>

              <Button type="submit" className="w-full h-11 bg-[#2D7D6F] text-white hover:bg-[#236B5F] font-semibold" disabled={loading} data-testid="login-submit-btn">
                {loading ? 'Signing in...' : 'Sign in'}
              </Button>
            </div>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6 font-body">
            Don't have an account?{' '}
            <Link to="/signup" className="text-[#2D7D6F] hover:underline font-semibold" data-testid="signup-link">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
